/**
 * @tyndo/ui-kit/domain/SupportBundleExport
 *
 * Closes G2.4 from docs/latest-analysis.md.
 *
 * Pulls a support-bundle ZIP from the gateway for a given simulator
 * run id (or production incident id). The contract on the backend
 * side is `ISupportBundleExportContract` (graduated to
 * `Tyndo.Core.Simulator`).
 *
 * The component shows the bundle's manifest summary inline (file
 * count, byte total, run start, assertion result), then offers a
 * Download button that streams the ZIP. For compliance use, the
 * bundle includes browser test recordings + simulator evidence +
 * API contract responses + backend events.
 *
 * Wire pattern at the call site:
 *
 * ```tsx
 * <SupportBundleExport
 *   runId={run.id}
 *   onFetch={async () => api.blob(`/api/v1/sim-bridge/support-bundle/${runId}`)}
 *   manifest={runManifest}
 *   defaultFilename={`support-bundle-${runId}.zip`}
 * />
 * ```
 */

import * as React from 'react';
import { Download, Package, FileArchive, CheckCircle2, XCircle } from 'lucide-react';
import { Button } from '../primitives/button';
import { Card } from '../primitives/card';
import { Badge } from '../primitives/badge';

export interface SupportBundleManifestSummary {
  fileCount: number;
  totalByteCount: number;
  runStartedAt: string | Date;
  runCapturedAt?: string | Date;
  allAssertionsPassed: boolean;
  failedAssertionCount: number;
  /** Optional content-type histogram for "what's in the bundle" surfacing. */
  fileCountByContentType?: Record<string, number>;
}

export interface SupportBundleExportProps {
  /** Run id (or incident id) the bundle is for. */
  runId: string;
  /** Optional manifest summary; rendered inline when present. */
  manifest?: SupportBundleManifestSummary;
  /** Imperative fetch returning a Blob. Caller wires this through useApiClient. */
  onFetch: () => Promise<Blob>;
  /** Default filename for the saved ZIP. */
  defaultFilename?: string;
  className?: string;
}

const formatBytes = (n: number): string => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

export const SupportBundleExport: React.FC<SupportBundleExportProps> = ({
  runId,
  manifest,
  onFetch,
  defaultFilename,
  className,
}) => {
  const [downloading, setDownloading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const onDownload = async () => {
    setDownloading(true);
    setError(null);
    try {
      const blob = await onFetch();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = defaultFilename ?? `support-bundle-${runId}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (ex) {
      setError(ex instanceof Error ? ex.message : String(ex));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card variant="elevated" className={className}>
      <div className="flex items-center gap-2 border-b border-border p-3">
        <Package size={16} className="text-primary" />
        <div className="flex flex-col">
          <span className="t-eyebrow text-muted-foreground">Support bundle</span>
          <span className="text-sm font-semibold text-foreground" data-run-id={runId}>
            Run {runId}
          </span>
        </div>
        <Button
          type="button"
          size="sm"
          onClick={onDownload}
          disabled={downloading}
          className="ml-auto flex items-center gap-1.5"
        >
          <Download size={14} />
          {downloading ? 'Downloading…' : 'Download ZIP'}
        </Button>
      </div>

      {manifest && (
        <div className="grid grid-cols-2 gap-3 p-3 text-sm">
          <ManifestStat
            label="Files"
            value={`${manifest.fileCount}`}
            icon={FileArchive}
          />
          <ManifestStat
            label="Size"
            value={formatBytes(manifest.totalByteCount)}
            icon={Package}
          />
          <ManifestStat
            label="Started"
            value={new Date(manifest.runStartedAt).toLocaleString()}
          />
          <ManifestStat
            label="Assertions"
            value={
              manifest.allAssertionsPassed ? (
                <span className="flex items-center gap-1 text-state-running">
                  <CheckCircle2 size={14} /> All pass
                </span>
              ) : (
                <span className="flex items-center gap-1 text-alarm-critical">
                  <XCircle size={14} /> {manifest.failedAssertionCount} failed
                </span>
              )
            }
          />
          {manifest.fileCountByContentType && (
            <div className="col-span-2 flex flex-wrap gap-1.5 pt-2">
              {Object.entries(manifest.fileCountByContentType).map(([ct, n]) => (
                <Badge key={ct} variant="info">
                  {ct}: {n}
                </Badge>
              ))}
            </div>
          )}
        </div>
      )}

      {error && (
        <p className="px-3 pb-3 text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
    </Card>
  );
};

interface ManifestStatProps {
  label: string;
  value: React.ReactNode;
  icon?: React.ComponentType<{ size?: number; className?: string }>;
}

const ManifestStat: React.FC<ManifestStatProps> = ({ label, value, icon: Icon }) => (
  <div>
    <div className="t-eyebrow text-muted-foreground">{label}</div>
    <div className="flex items-center gap-1.5 text-foreground">
      {Icon ? <Icon size={14} className="text-muted-foreground" /> : null}
      <span>{value}</span>
    </div>
  </div>
);
